import {
  type AddFileBody,
  addFile,
  type FileData,
  getFiles,
} from "@/services/files";
import { create } from "zustand";
import { t } from "i18next";
import { uploadAudioToS3, type UploadResult } from "@/lib/s3/uploadAudioS3";

export interface VisitsState {
  files: FileData[];
  loading: boolean;
  error: string | null;
  getFiles: (
    body: { userId: number; sparse: number },
    showLoading?: boolean
  ) => Promise<void>;
  // audio files upload
  loadingUploadFiles: boolean;
  errorUploadFiles: string | null;
  uploadAndAddFile: (parameters: {
    file: File | Blob;
    userId: number;
    duration: number;
    style: string;
    format: string;
    exam: string;
    template: string;
    macros?: string;
    customTemplateId: number | null;
  }) => Promise<void>;
  validateFileStatus: (userId: number, fileId: number) => Promise<void>;
}

export const useVisitsStore = create<VisitsState>()((set) => ({
  files: [],
  loading: false,
  error: null,
  // audio files upload
  loadingUploadFiles: false,
  errorUploadFiles: null,

  getFiles: async (
    body: { userId: number; sparse: number },
    showLoading: boolean = true
  ): Promise<void> => {
    set({ loading: showLoading, error: null });
    try {
      const { files, error } = await getFiles({
        userId: body.userId,
        sparse: body.sparse,
      });
      if (!error) {
        // TODO: we need do this on backend
        const sortedFiles = files?.sort((a, b) => b.rowno - a.rowno);
        set({ files: sortedFiles, loading: false });
        return;
      } else {
        set({ error: t("error.unknown"), files: [], loading: false });
        return;
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        set({ files: [], error: error.message, loading: false });
      } else {
        set({
          files: [],
          error: t("error.unknown"),
          loading: false,
        });
      }
    }
  },

  uploadAndAddFile: async (parameters): Promise<void> => {
    set({ loadingUploadFiles: true, errorUploadFiles: null });
    try {
      // 1: upload file to S3
      const uploadResult: UploadResult = await uploadAudioToS3(
        parameters.file,
        parameters.userId
      );

      if (!uploadResult.ok || !uploadResult.key) {
        set({
          loadingUploadFiles: false,
          errorUploadFiles: uploadResult.error || "Error al subir el archivo",
        });
        return;
      }

      // 2: add file to database
      const addFilePayload: AddFileBody = {
        userId: parameters.userId,
        name: uploadResult.key,
        awsBucket: import.meta.env.VITE_S3_BUCKET_URL || "",
        duration: parameters.duration,
        style: parameters.style,
        format: parameters.format,
        exam: parameters.exam,
        template: parameters.template,
        macros: parameters.macros || "",
        customTemplateId: parameters.customTemplateId,
      };

      const addFileResponse = await addFile(addFilePayload);

      if (addFileResponse.error) {
        set({
          loadingUploadFiles: false,
          errorUploadFiles:
            addFileResponse.message || "Error al agregar el archivo",
        });
        return;
      }

      set({ loadingUploadFiles: false, errorUploadFiles: null });
    } catch (error: unknown) {
      set({
        loadingUploadFiles: false,
        errorUploadFiles:
          error instanceof Error ? error.message : t("error.unknown"),
      });
    }
  },

  validateFileStatus: async (userId: number, fileId: number): Promise<void> => {
    try {
      const { files, error } = await getFiles({ userId, fileId });
      if (!error && files && files.length > 0) {
        set((state) => {
          const updatedFiles = state.files.map((file) =>
            file.id === fileId ? files[0]! : file
          );
          return { files: updatedFiles };
        });
      }
    } catch {
      // do nothing for now
    }
  },
}));
